import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ApiError, api, type ExtractionCapabilities } from '../../api/client';
import type { ExtractionResult } from '../../types';
import { Button, Notice, RemoveButton, Section, Spinner, cx } from '../ui/primitives';

/**
 * Attachment upload and extraction.
 *
 * Three ways in, because operators have the brief in whatever form the client
 * sent it: drag-and-drop, a file picker, and Ctrl+V of a screenshot straight
 * from the clipboard.
 *
 * Extraction runs immediately on drop rather than at Continue, so the user sees
 * what was recovered — and what was not — while they can still fix it.
 */

const MEGABYTE = 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes >= MEGABYTE) return `${Math.round(bytes / MEGABYTE)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** A file being extracted, or one that failed. */
interface PendingAttachment {
  key: string;
  fileName: string;
  error?: string;
}

const METHOD_LABEL: Record<ExtractionResult['method'], string> = {
  DETERMINISTIC: 'read directly from the sheet',
  GOOGLE_DRIVE_OCR: 'read by Google Drive OCR',
  VISION_MODEL: 'read by Claude vision',
};

function AttachmentCard({
  attachment,
  onRemove,
  onUseText,
}: {
  attachment: ExtractionResult;
  onRemove: () => void;
  onUseText?: (text: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [textUsed, setTextUsed] = useState(false);
  const [textShown, setTextShown] = useState(false);
  const cohortCount = attachment.cohorts.length;
  const interpreted = attachment.method !== 'DETERMINISTIC';

  return (
    <li className="rounded-input border border-line bg-surface">
      <div className="flex items-start gap-3 px-4 py-3">
        <span
          aria-hidden="true"
          className={cx(
            'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-button text-[11px] font-semibold',
            interpreted ? 'bg-accent-soft text-accent' : 'bg-success-soft text-success',
          )}
        >
          {attachment.kind === 'IMAGE' ? 'IMG' : 'XLS'}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-field text-ink" title={attachment.fileName}>
            {attachment.fileName}
          </p>
          <p className="mt-0.5 text-micro text-ink-muted">
            {cohortCount === 0
              ? 'No cohorts found'
              : `${cohortCount} cohort${cohortCount === 1 ? '' : 's'} found`}
            {' · '}
            {METHOD_LABEL[attachment.method]}
            {attachment.usage
              ? ` · ${attachment.usage.inputTokens.toLocaleString()} in / ${attachment.usage.outputTokens.toLocaleString()} out tokens`
              : ''}
          </p>

          {cohortCount ? (
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              className="mt-1.5 text-micro font-medium text-accent hover:underline"
            >
              {expanded ? 'Hide what was found' : 'Show what was found'}
            </button>
          ) : null}

          {expanded ? (
            <ul className="mt-2 space-y-2 border-t border-line-soft pt-2">
              {attachment.cohorts.map((cohort, index) => (
                <li key={`${attachment.id}-${index}`} className="text-label">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium text-ink">
                      {cohort.name || <span className="italic text-ink-faint">Unnamed cohort</span>}
                    </span>
                    <span className="shrink-0 text-ink-muted">{cohort.size || '—'}</span>
                  </div>
                  {cohort.signals.length ? (
                    <ul className="mt-0.5 space-y-0.5">
                      {cohort.signals.map((signal, signalIndex) => (
                        <li
                          key={`${attachment.id}-${index}-${signalIndex}`}
                          className="flex gap-1.5 text-ink-soft"
                        >
                          <span aria-hidden="true" className="mt-[3px] shrink-0 leading-none">
                            &bull;
                          </span>
                          <span className="min-w-0 break-words">{signal}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-ink-faint italic">No signals read</p>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          {attachment.notes.length ? (
            <ul className="mt-2 space-y-0.5">
              {attachment.notes.map((note) => (
                <li key={note} className="text-micro text-warning">
                  {note}
                </li>
              ))}
            </ul>
          ) : null}

          {/*
            The OCR'd text is offered even when row detection succeeded. It turns
            a failed read from a dead end into a head start the operator edits,
            rather than making them retype the screenshot.
          */}
          {attachment.rawText ? (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                {onUseText ? (
                  <button
                    type="button"
                    disabled={textUsed}
                    onClick={() => {
                      onUseText(attachment.rawText!);
                      setTextUsed(true);
                    }}
                    className="rounded-button border border-accent px-3 py-1 text-micro font-medium text-accent transition-colors hover:bg-accent-soft disabled:cursor-default disabled:border-line disabled:text-ink-faint"
                  >
                    {textUsed ? 'Added to the brief above' : 'Add the read text to the brief'}
                  </button>
                ) : null}

                {/*
                  Showing the text is what makes a failed read diagnosable: the
                  reviewer can see at a glance whether OCR misread the image or
                  simply captured something with no cohorts in it.
                */}
                <button
                  type="button"
                  onClick={() => setTextShown((current) => !current)}
                  className="text-micro font-medium text-accent hover:underline"
                >
                  {textShown ? 'Hide the text that was read' : 'Show the text that was read'}
                </button>

                <span className="text-micro text-ink-faint">
                  {attachment.rawText.length.toLocaleString()} characters read
                </span>
              </div>

              {textShown ? (
                <pre className="scroll-thin max-h-56 overflow-auto rounded-input border border-line bg-canvas px-3 py-2 text-[11px] leading-5 text-ink-soft">
                  {attachment.rawText}
                </pre>
              ) : null}
            </div>
          ) : null}
        </div>

        <RemoveButton onClick={onRemove} label={`Remove ${attachment.fileName}`} />
      </div>
    </li>
  );
}


/**
 * Whether this browser can be *asked* for the clipboard's contents.
 *
 * `Ctrl+V` is a keyboard event, which a phone has no way to send. The async
 * clipboard API can be read on demand instead — behind a permission prompt — so
 * a button can do on a phone what a keystroke does on a desktop.
 */
function canReadClipboard(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.clipboard?.read === 'function';
}

/**
 * Pulls any image sitting on the clipboard.
 *
 * Returns an empty list rather than throwing when there is nothing to take or
 * permission is refused: a failed paste is an ordinary outcome, not an error the
 * user needs a stack trace for.
 */
async function readClipboardImages(): Promise<File[]> {
  if (!canReadClipboard()) return [];

  const items = await navigator.clipboard.read();
  const files: File[] = [];

  for (const item of items) {
    const type = item.types.find((candidate) => candidate.startsWith('image/'));
    if (!type) continue;

    const blob = await item.getType(type);
    const extension = type.split('/')[1] ?? 'png';
    files.push(new File([blob], `pasted-image.${extension}`, { type }));
  }

  return files;
}

export function AttachmentsSection({
  attachments,
  onChange,
  onUseText,
}: {
  attachments: ExtractionResult[];
  onChange: (attachments: ExtractionResult[]) => void;
  /** Appends text read from an image into the brief box. */
  onUseText?: (text: string) => void;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  /* An image-only picker, kept apart from the any-file one below it. */
  const imageRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const [capabilities, setCapabilities] = useState<ExtractionCapabilities | null>(null);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const [pasting, setPasting] = useState(false);
  /** Why a paste produced nothing, when it produced nothing. */
  const [pasteNote, setPasteNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getExtractionCapabilities()
      .then((result) => {
        if (!cancelled) setCapabilities(result);
      })
      .catch(() => {
        // Extraction is optional; a failed probe just leaves the panel minimal.
        if (!cancelled) setCapabilities(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const accept = capabilities
    ? [
        ...capabilities.spreadsheet.extensions,
        ...(capabilities.image.enabled ? capabilities.image.mediaTypes : []),
      ].join(',')
    : '.xlsx,.xls,.csv,image/png,image/jpeg';

  // `extract` runs across awaits, so it reads the list through a ref rather
  // than closing over a snapshot that goes stale mid-upload.
  const attachmentsRef = useRef(attachments);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  const extract = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      for (const file of files) {
        const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
        setPending((current) => [...current, { key, fileName: file.name }]);

        try {
          const result = await api.extractAttachment(file);
          // Read the latest list at commit time so two concurrent uploads
          // cannot clobber each other.
          onChange([...attachmentsRef.current, result]);
          setPending((current) => current.filter((entry) => entry.key !== key));
        } catch (err) {
          const message =
            err instanceof ApiError ? err.message : 'That file could not be read.';
          setPending((current) =>
            current.map((entry) => (entry.key === key ? { ...entry, error: message } : entry)),
          );
        }
      }
    },
    [onChange],
  );

  /**
   * Clipboard paste. Scoped to the drop zone rather than the document, so
   * pasting a brief into the textarea is never intercepted.
   */
  useEffect(() => {
    const node = dropZoneRef.current;
    if (!node) return;

    const onPaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? []);
      const images = files.filter((file) => file.type.startsWith('image/'));
      if (!images.length) return;

      event.preventDefault();
      void extract(images);
    };

    node.addEventListener('paste', onPaste);
    return () => node.removeEventListener('paste', onPaste);
  }, [extract]);

  /**
   * Takes an image from the clipboard on demand.
   *
   * The button exists because `Ctrl+V` is a keystroke a phone cannot send. Every
   * way this can come up short — no support, permission refused, nothing on the
   * clipboard — is reported as a plain sentence rather than an error, because
   * none of them is a fault the user can debug.
   */
  const pasteFromClipboard = useCallback(async () => {
    setPasteNote(null);

    if (!canReadClipboard()) {
      setPasteNote(
        'This browser will not let a page read the clipboard. Use Choose an image instead, or press Ctrl+V on a computer.',
      );
      return;
    }

    setPasting(true);
    try {
      const images = await readClipboardImages();

      if (!images.length) {
        setPasteNote('There is no image on the clipboard. Copy a screenshot first, then try again.');
        return;
      }

      await extract(images);
    } catch {
      setPasteNote(
        'The clipboard could not be read — permission may have been declined. Use Choose an image instead.',
      );
    } finally {
      setPasting(false);
    }
  }, [extract]);

  const imageEnabled = capabilities?.image.enabled ?? false;
  const limit = capabilities?.maxUploadBytes ?? 12 * MEGABYTE;

  return (
    <Section
      title="Attachments"
      hint="(optional)"
      action={
        <span className="text-micro text-ink-faint">
          {imageEnabled ? 'Spreadsheet or image' : 'Spreadsheet'} &middot; up to {formatBytes(limit)}
        </span>
      }
    >
      {/*
        Two ways in that work without a keyboard. A phone has no Ctrl+V, so the
        photo library gets its own button and the clipboard can be read on
        demand; the drop zone and the keystroke stay for a desktop.
      */}
      {imageEnabled ? (
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Button
            variant="secondary"
            className="justify-center py-2.5"
            onClick={() => imageRef.current?.click()}
          >
            Choose an image
          </Button>

          <Button
            variant="secondary"
            className="justify-center py-2.5"
            loading={pasting}
            onClick={() => {
              void pasteFromClipboard();
            }}
          >
            Paste from clipboard
          </Button>
        </div>
      ) : null}

      {pasteNote ? (
        <div className="mb-3">
          <Notice tone="info" title="Nothing pasted">
            {pasteNote}
          </Notice>
        </div>
      ) : null}

      {/*
        Image-only, so a phone's picker offers the photo library — and its camera,
        if the reader wants it — rather than the whole file system.
      */}
      <input
        ref={imageRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => {
          void extract(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />

      {/*
        The drop zone is focusable so a keyboard user can tab to it and paste,
        which is the only way clipboard paste is reachable without a mouse.
      */}
      <div
        ref={dropZoneRef}
        tabIndex={0}
        role="button"
        aria-describedby={`${inputId}-help`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void extract(Array.from(event.dataTransfer.files));
        }}
        className={cx(
          'cursor-pointer rounded-input border border-dashed px-4 py-7 text-center transition-colors',
          'focus-visible:ring-2 focus-visible:ring-accent/40',
          dragging ? 'border-accent bg-accent-soft' : 'border-line-strong bg-surface hover:border-accent',
        )}
      >
        <p className="text-field text-ink">
          Drop a file here, or <span className="font-medium text-accent">browse</span>
        </p>
        <p id={`${inputId}-help`} className="mt-1 text-micro text-ink-muted">
          {imageEnabled
            ? 'Excel, CSV, or a screenshot. On a computer you can also click here and press Ctrl+V.'
            : capabilities?.image.requiresGoogleSignIn
              ? 'Excel or CSV. Sign in with Google above to read screenshots too, using Drive OCR at no extra cost.'
              : 'Excel or CSV. Image extraction is not configured on this server.'}
        </p>
        <p className="mt-1.5 text-micro text-ink-faint">
          Cohorts, audience sizes and signals are read out and added to the form for you to check.
        </p>

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(event) => {
            void extract(Array.from(event.target.files ?? []));
            // Reset so re-selecting the same file fires change again.
            event.target.value = '';
          }}
        />
      </div>

      {pending.length ? (
        <ul className="space-y-2">
          {pending.map((entry) => (
            <li
              key={entry.key}
              className={cx(
                'flex items-center gap-3 rounded-input border px-4 py-3',
                entry.error ? 'border-danger/30 bg-danger-soft' : 'border-line bg-surface',
              )}
            >
              {entry.error ? (
                <span aria-hidden="true" className="text-danger">
                  !
                </span>
              ) : (
                <Spinner className="h-4 w-4 shrink-0 text-accent" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-field text-ink">{entry.fileName}</p>
                <p className="text-micro text-ink-muted">
                  {entry.error ?? 'Reading the file…'}
                </p>
              </div>
              {entry.error ? (
                <Button
                  variant="ghost"
                  onClick={() =>
                    setPending((current) => current.filter((item) => item.key !== entry.key))
                  }
                  className="shrink-0 text-micro"
                >
                  Dismiss
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {attachments.length ? (
        <ul className="space-y-2">
          {attachments.map((attachment) => (
            <AttachmentCard
              key={attachment.id}
              attachment={attachment}
              onRemove={() =>
                onChange(attachments.filter((entry) => entry.id !== attachment.id))
              }
              {...(onUseText ? { onUseText } : {})}
            />
          ))}
        </ul>
      ) : null}

      {attachments.some((attachment) => attachment.method !== 'DETERMINISTIC') ? (
        <Notice tone="warning" title="Check the values read from images">
          Text in a screenshot is interpreted, not copied, so a figure can be misread. Anything
          extracted from an image is editable on the next screen — confirm each cohort against the
          original before submitting.
        </Notice>
      ) : null}
    </Section>
  );
}
